import cloudUploadIcon from "@/assets/cloud-upload.svg";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { FileSpreadsheet, Loader2, RotateCcw, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { catchError } from "@/lib/errors";
import { dilemmasRepo } from "@/lib/repositories";
import { cn } from "@/lib/utils";
import { queryClient } from "@/lib/query-client";
import { handleApiError } from "@/utils/errors";
import type { ApiDilemmasImportFromExcelResponse } from "@/features/dilemmas/types";

const MAX_SIZE = 25 * 1024 * 1024; // 25 MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

const ImportFromExcelDialog = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] =
    useState<ApiDilemmasImportFromExcelResponse | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const next = acceptedFiles[0];
      if (!next) return;

      if (next.size > MAX_SIZE) {
        toast.error(t("modules.dilemmas.import.file_too_large"));
        return;
      }

      setResult(null);
      setUploadProgress(null);
      setFile(next);
    },
    [t],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      multiple: false,
      maxSize: MAX_SIZE,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
        "text/csv": [".csv"],
        "application/csv": [".csv"],
      },
    });

  const rejectionMessage = useMemo(() => {
    const first = fileRejections[0];
    const firstError = first?.errors?.[0]?.code;

    if (!firstError) return null;
    if (firstError === "file-too-large")
      return t("modules.dilemmas.import.file_too_large");
    if (firstError === "file-invalid-type")
      return t("modules.dilemmas.import.file_invalid_type");
    return t("errors.generic");
  }, [fileRejections, t]);

  const resetState = () => {
    setFile(null);
    setUploadProgress(null);
    setIsSubmitting(false);
    setResult(null);
  };

  const onOpenChange = (next: boolean) => {
    // Refresh the lists on close so the freshly imported situations show up.
    if (!next && (result?.summary.success_count ?? 0) > 0) {
      void queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
      void queryClient.invalidateQueries({ queryKey: ["submissions"] });
    }
    setOpen(next);
    if (!next) resetState();
  };

  const onSubmit = async () => {
    if (!file) {
      toast.error(t("modules.dilemmas.import.file_required"));
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    const [error, data] = await catchError(
      dilemmasRepo.importFromExcel(file, (p) => setUploadProgress(p)),
    );

    if (error) {
      toast.error(handleApiError(error).message);
      setIsSubmitting(false);
      setUploadProgress(null);
      return;
    }

    setResult(data);
    setIsSubmitting(false);
    setUploadProgress(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload />
          <span className="hidden md:inline">
            {t("modules.dilemmas.import.open_button")}
          </span>
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("modules.dilemmas.import.title")}</DialogTitle>
          <DialogDescription>
            {t("modules.dilemmas.import.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {!file && (
            <div
              {...getRootProps()}
              className={cn(
                "cursor-pointer p-4 py-10 border border-dashed rounded-md flex flex-col gap-2 items-center justify-center transition-colors",
                isDragActive
                  ? "border-primary bg-primary/5"
                  : "border-gray-300",
              )}
            >
              <input {...getInputProps()} />
              <img src={cloudUploadIcon} alt="" className="size-6" />
              <p className="text-gray-500 text-sm text-center">
                <span className="text-indigo-600">
                  {t("modules.dilemmas.import.upload_click")}
                </span>{" "}
                {t("modules.dilemmas.import.upload_drag")}
                <br />
                <span className="text-xs">
                  {t("modules.dilemmas.import.upload_help")}
                </span>
              </p>
            </div>
          )}

          {rejectionMessage && (
            <div className="text-sm text-red-600">{rejectionMessage}</div>
          )}

          {file && (
            <div className="flex items-center gap-3 p-3 border rounded-md">
              <FileSpreadsheet className="size-4 text-gray-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(file.size)}
                </p>
                {isSubmitting && uploadProgress !== null && (
                  <div className="mt-2 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </div>
              {!result && (
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    setFile(null);
                    setResult(null);
                  }}
                  className="shrink-0 p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-50"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
          )}

          {result && (
            <div className="flex flex-col gap-4">
              <div className="rounded-md border p-4">
                <div className="font-medium">
                  {t("modules.dilemmas.import.summary_title")}
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500">
                      {t("modules.dilemmas.import.summary.lines_found")}
                    </div>
                    <div className="font-medium">
                      {result.summary.lines_found}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">
                      {t("modules.dilemmas.import.summary.success_count")}
                    </div>
                    <div className="font-medium text-green-600">
                      {result.summary.success_count}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">
                      {t("modules.dilemmas.import.summary.failure_count")}
                    </div>
                    <div
                      className={cn(
                        "font-medium",
                        result.summary.failure_count > 0 && "text-destructive",
                      )}
                    >
                      {result.summary.failure_count}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">
                      {t("modules.dilemmas.import.summary.lines_processed")}
                    </div>
                    <div className="font-medium">
                      {result.summary.lines_processed}
                    </div>
                  </div>
                </div>
              </div>

              {result.errors.length > 0 && (
                <div className="rounded-md border">
                  <div className="p-4 border-b font-medium text-destructive">
                    {t("modules.dilemmas.import.errors_title")}
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          {t("modules.dilemmas.import.errors.row")}
                        </TableHead>
                        <TableHead>
                          {t("modules.dilemmas.import.errors.context")}
                        </TableHead>
                        <TableHead>
                          {t("modules.dilemmas.import.errors.details")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.errors.map((err) => (
                        <TableRow
                          key={`${err.excel_row}-${JSON.stringify(err.context)}`}
                        >
                          <TableCell>{err.excel_row}</TableCell>
                          <TableCell className="whitespace-normal">
                            {Object.entries(err.context)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(" · ")}
                          </TableCell>
                          <TableCell className="whitespace-normal">
                            <div className="flex flex-col gap-2">
                              {err.errors.map((e, idx) => (
                                <div key={`${e.code}-${idx}`}>
                                  <div className="text-xs text-gray-500">
                                    {e.column}
                                  </div>
                                  <div className="text-sm">{e.message}</div>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="items-center">
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {result ? t("modules.dilemmas.import.close") : t("keys.cancel")}
          </Button>
          {result ? (
            <Button type="button" onClick={resetState}>
              <RotateCcw />
              {t("modules.dilemmas.import.reset")}
            </Button>
          ) : (
            <Button onClick={onSubmit} disabled={isSubmitting || !file}>
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" />
                  {t("modules.dilemmas.import.submitting")}
                </>
              ) : (
                t("modules.dilemmas.import.submit")
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFromExcelDialog;
